import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Minimize2, Maximize2, Loader2, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import { Geolocation } from '@capacitor/geolocation';
import { createColoredIcon, type MarkerColor } from '@/lib/map-icons';

// Dynamically load Leaflet CSS only when the map is used (keeps it off the landing page critical path)
const ensureLeafletCss = () => {
    if (typeof document === 'undefined') return;
    const id = 'leaflet-css';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);
};

// Fix for default marker icon in Leaflet with bundlers
const fixLeafletIcon = () => {
    delete (L.Icon.Default.prototype as any)._getIconUrl;

    L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    });
};

// Call it once
ensureLeafletCss();
fixLeafletIcon();

interface MapPickerProps {
    latitude?: number | null;
    longitude?: number | null;
    onLocationSelect?: (lat: number, lng: number) => void;
    readOnly?: boolean;
    /** Optional secondary marker (e.g. patient's residence). */
    secondaryLatitude?: number | null;
    secondaryLongitude?: number | null;
    /** Color of the primary (latitude/longitude) marker. */
    primaryColor?: MarkerColor;
    /** Color of the secondary marker. */
    secondaryColor?: MarkerColor;
    /** Popup HTML/text bound to the primary marker. */
    primaryPopup?: string;
    /** Popup HTML/text bound to the secondary marker. */
    secondaryPopup?: string;
}

function getIcon(color?: MarkerColor): L.Icon | undefined {
    if (!color || color === 'default') return undefined;
    return createColoredIcon(color);
}

function applyMarker(
    map: L.Map,
    ref: React.MutableRefObject<L.Marker | null>,
    position: [number, number] | null,
    color?: MarkerColor,
    popup?: string
) {
    if (!position) {
        if (ref.current) {
            ref.current.remove();
            ref.current = null;
        }
        return;
    }
    const icon = getIcon(color);
    if (ref.current) {
        ref.current.setLatLng(position);
        if (icon) ref.current.setIcon(icon);
    } else {
        ref.current = icon
            ? L.marker(position, { icon }).addTo(map)
            : L.marker(position).addTo(map);
    }
    if (popup) {
        ref.current.bindPopup(popup);
    } else {
        ref.current.unbindPopup();
    }
}

// Standalone component to prevent re-mounting on parent state changes
function FullscreenMap({
    position,
    secondaryPosition,
    primaryColor,
    secondaryColor,
    primaryPopup,
    secondaryPopup,
    defaultPosition,
    onLocationSelect,
    readOnly
}: {
    position: [number, number] | null;
    secondaryPosition: [number, number] | null;
    primaryColor?: MarkerColor;
    secondaryColor?: MarkerColor;
    primaryPopup?: string;
    secondaryPopup?: string;
    defaultPosition: [number, number];
    onLocationSelect?: (lat: number, lng: number) => void;
    readOnly?: boolean;
}) {
    const fsMapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);
    const secondaryMarkerRef = useRef<L.Marker | null>(null);

    // Initial Setup
    useEffect(() => {
        if (!fsMapContainerRef.current) return;
        if (mapInstanceRef.current) return;

        const center = position || defaultPosition;
        const map = L.map(fsMapContainerRef.current).setView(center, 15);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map);

        mapInstanceRef.current = map;

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                markerRef.current = null;
                secondaryMarkerRef.current = null;
            }
        };
    }, []);

    // Event Listener Binding (updates with fresh props)
    const propsRef = useRef({ readOnly, onLocationSelect });
    useEffect(() => {
        propsRef.current = { readOnly, onLocationSelect };
    }, [readOnly, onLocationSelect]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;

        map.off('click');
        map.on('click', (e: L.LeafletMouseEvent) => {
            const { readOnly, onLocationSelect } = propsRef.current;
            if (!readOnly && onLocationSelect) {
                onLocationSelect(e.latlng.lat, e.latlng.lng);
            }
        });
    }, []);

    // Position & Marker Updates
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;

        applyMarker(map, markerRef, position, primaryColor, primaryPopup);
        applyMarker(map, secondaryMarkerRef, secondaryPosition, secondaryColor, secondaryPopup);

        if (position && secondaryPosition) {
            const bounds = L.latLngBounds([position, secondaryPosition]);
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
        } else if (position) {
            const currentZoom = map.getZoom();
            const targetZoom = currentZoom < 10 ? 15 : currentZoom;
            map.setView(position, targetZoom, { animate: true });
        }
    }, [position?.[0], position?.[1], secondaryPosition?.[0], secondaryPosition?.[1], primaryColor, secondaryColor, primaryPopup, secondaryPopup]);

    return <div ref={fsMapContainerRef} className="w-full h-full" />;
}

export function MapPicker({
    latitude,
    longitude,
    onLocationSelect,
    readOnly = false,
    secondaryLatitude,
    secondaryLongitude,
    primaryColor,
    secondaryColor,
    primaryPopup,
    secondaryPopup,
}: MapPickerProps) {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);
    const secondaryMarkerRef = useRef<L.Marker | null>(null);

    const [fullscreenOpen, setFullscreenOpen] = useState(false);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Default central location (São Paulo)
    const defaultPosition: [number, number] = [-23.5505, -46.6333];
    const position: [number, number] | null = (latitude != null && longitude != null)
        ? [latitude, longitude]
        : null;
    const secondaryPosition: [number, number] | null = (secondaryLatitude != null && secondaryLongitude != null)
        ? [secondaryLatitude, secondaryLongitude]
        : null;

    useEffect(() => {
        setMounted(true);
    }, []);

    // Initialize Map
    useEffect(() => {
        if (!mounted || !mapContainerRef.current) return;
        if (mapInstanceRef.current) return;

        const initialCenter = position || defaultPosition;
        const map = L.map(mapContainerRef.current, {
            zoomControl: false
        }).setView(initialCenter, 13);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map);

        mapInstanceRef.current = map;

        // Initial markers
        applyMarker(map, markerRef, position, primaryColor, primaryPopup);
        applyMarker(map, secondaryMarkerRef, secondaryPosition, secondaryColor, secondaryPopup);

        if (position && secondaryPosition) {
            map.fitBounds(L.latLngBounds([position, secondaryPosition]), { padding: [30, 30], maxZoom: 16 });
        }

        setTimeout(() => {
            map.invalidateSize();
        }, 100);

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                markerRef.current = null;
                secondaryMarkerRef.current = null;
            }
        };
    }, [mounted]);

    // Event Listener Binding for Main Map
    const propsRef = useRef({ readOnly, onLocationSelect });
    useEffect(() => {
        propsRef.current = { readOnly, onLocationSelect };
    }, [readOnly, onLocationSelect]);

    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;

        map.off('click');
        map.on('click', (e: L.LeafletMouseEvent) => {
            const { readOnly, onLocationSelect } = propsRef.current;
            if (!readOnly && onLocationSelect) {
                onLocationSelect(e.latlng.lat, e.latlng.lng);
            }
        });
    }, [mounted]);

    // Update markers and view when positions change
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;

        applyMarker(map, markerRef, position, primaryColor, primaryPopup);
        applyMarker(map, secondaryMarkerRef, secondaryPosition, secondaryColor, secondaryPopup);

        if (position && secondaryPosition) {
            const bounds = L.latLngBounds([position, secondaryPosition]);
            map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
        } else if (position) {
            const currentZoom = map.getZoom();
            const targetZoom = currentZoom < 10 ? 15 : currentZoom;
            map.setView(position, targetZoom, { animate: true });
        }
    }, [latitude, longitude, secondaryLatitude, secondaryLongitude, primaryColor, secondaryColor, primaryPopup, secondaryPopup]);


    const handleGetCurrentLocation = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setIsGettingLocation(true);
        try {
            const permissionStatus = await Geolocation.checkPermissions();

            if (permissionStatus.location !== 'granted') {
                const request = await Geolocation.requestPermissions();
                if (request.location !== 'granted') {
                    toast.error("Permissão de localização negada.");
                    setIsGettingLocation(false);
                    return;
                }
            }

            const coordinates = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 3000
            });

            if (onLocationSelect) {
                onLocationSelect(coordinates.coords.latitude, coordinates.coords.longitude);
                toast.success("Localização atualizada!");
            }
        } catch (err: any) {
            console.error('Error getting location:', err);

            if ('geolocation' in navigator) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        if (onLocationSelect) {
                            onLocationSelect(position.coords.latitude, position.coords.longitude);
                            toast.success("Localização atualizada!");
                        }
                        setIsGettingLocation(false);
                    },
                    (error) => {
                        console.error('Browser geolocation error:', error);
                        toast.error("Não foi possível obter sua localização.");
                        setIsGettingLocation(false);
                    },
                    { enableHighAccuracy: true, timeout: 10000 }
                );
                return;
            } else {
                toast.error("Erro ao obter localização: " + (err.message || "Desconhecido"));
            }
        } finally {
            if (isGettingLocation) setIsGettingLocation(false);
        }
    };

    if (!mounted) return <div className="w-full h-[200px] bg-muted/20 animate-pulse rounded-md border" />;

    return (
        <div className="space-y-2">
            <div className="relative w-full h-[200px] rounded-md overflow-hidden border">
                <div ref={mapContainerRef} className="w-full h-full z-0" />

                <div className="absolute bottom-2 right-2 z-[400] flex flex-col gap-2 pointer-events-auto">
                    {!readOnly && (
                        <Button
                            variant="secondary"
                            size="icon"
                            className="h-8 w-8 bg-white text-gray-900 shadow-md hover:bg-gray-100"
                            title="Usar minha localização"
                            onClick={handleGetCurrentLocation}
                            disabled={isGettingLocation}
                        >
                            {isGettingLocation ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Navigation className="h-4 w-4 text-primary" />}
                        </Button>
                    )}
                </div>

                <div className="absolute top-2 right-2 z-[400] pointer-events-auto">
                    <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
                        {!fullscreenOpen && (
                            <Button
                                variant="secondary"
                                size="icon"
                                className="h-8 w-8 bg-white text-gray-900 shadow-md hover:bg-gray-100 opacity-90 hover:opacity-100"
                                title="Maximizar mapa"
                                onClick={(event) => {
                                    event.preventDefault();
                                    setFullscreenOpen(true);
                                }}
                            >
                                <Maximize2 className="h-4 w-4" />
                            </Button>
                        )}
                        <DialogContent className="max-w-[90vw] h-[90vh] p-0 overflow-hidden flex flex-col">
                            <DialogTitle className="sr-only">Mapa Expandido</DialogTitle>
                            <DialogDescription className="sr-only">Visualização detalhada do mapa de localização</DialogDescription>
                            <div className="relative w-full h-full">
                                <FullscreenMap
                                    position={position}
                                    secondaryPosition={secondaryPosition}
                                    primaryColor={primaryColor}
                                    secondaryColor={secondaryColor}
                                    primaryPopup={primaryPopup}
                                    secondaryPopup={secondaryPopup}
                                    defaultPosition={defaultPosition}
                                    onLocationSelect={onLocationSelect}
                                    readOnly={readOnly}
                                />
                                <Button
                                    variant="secondary"
                                    size="icon"
                                    className="absolute top-4 right-4 z-[400] h-10 w-10 bg-white text-gray-900 shadow-md hover:bg-gray-100"
                                    onClick={() => setFullscreenOpen(false)}
                                >
                                    <Minimize2 className="h-5 w-5" />
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {readOnly ? (
                <p className="text-xs text-muted-foreground text-center">
                    {latitude != null && longitude != null ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : 'Sem localização definida'}
                </p>
            ) : (
                <p className="text-xs text-muted-foreground text-center">
                    {latitude != null && longitude != null
                        ? `Local selecionado: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
                        : 'Clique no mapa para definir a localização ou use o botão de GPS'}
                </p>
            )}
        </div>
    );
}
